import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload, FileImage, CheckCircle2, AlertCircle, Loader2, Trash2, Eye,
  Star, Info,
} from "lucide-react";
import api, { formatErr } from "../lib/api";

const APPLY_KINDS = [
  { key: "both", label: "PFD + P&ID" },
  { key: "pfd",  label: "PFD only" },
  { key: "pid",  label: "P&ID only" },
];

export default function DrawingTemplatePanel({ projectId, defaultApplyTo = "both" }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [previewSvg, setPreviewSvg] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [applyTo, setApplyTo] = useState(defaultApplyTo);
  const inputRef = useRef(null);

  const loadList = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/projects/${projectId}/drawing-templates`);
      setTemplates(data.templates || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadList(); }, [loadList]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    setOk("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/projects/${projectId}/drawing-templates/upload`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { template_name: file.name.replace(/\.[^.]+$/, ""), apply_to: applyTo },
        });
      setOk(`Uploaded "${data.template_name}" · ${data.canvas_width}×${data.canvas_height} · ${data.confidence_pct}% confidence (${data.extracted_from})`);
      await loadList();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleActivate = async (tpl) => {
    setErr("");
    setOk("");
    try {
      await api.post(
        `/projects/${projectId}/drawing-templates/${tpl.template_id}/activate`,
        { kind: tpl.apply_to || applyTo });
      setOk(`✓ Activated "${tpl.template_name}" for ${tpl.apply_to}. Every new PFD / P&ID / DXF export will use this template.`);
      await loadList();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.template_name}"?`)) return;
    setErr("");
    try {
      await api.delete(
        `/projects/${projectId}/drawing-templates/${tpl.template_id}`);
      await loadList();
      if (previewingId === tpl.template_id) {
        setPreviewingId(null);
        setPreviewSvg("");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const handlePreview = async (tpl) => {
    if (previewingId === tpl.template_id) {
      setPreviewingId(null);
      setPreviewSvg("");
      return;
    }
    setPreviewingId(tpl.template_id);
    setPreviewSvg("");
    try {
      const { data } = await api.get(
        `/projects/${projectId}/drawing-templates/${tpl.template_id}/preview`);
      setPreviewSvg(data.svg || "");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
      setPreviewingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="drawing-template-panel">
      {/* Header */}
      <div className="rounded-lg border border-fuchsia-900/40 bg-fuchsia-950/20 p-4">
        <div className="flex items-start gap-3">
          <FileImage className="h-5 w-5 text-fuchsia-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-fuchsia-100">
              Company drawing template
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Upload your corporate PFD / P&amp;ID border, title block or DXF
              template. Once activated, every generated PFD, P&amp;ID and
              AutoCAD export in this project uses your standard — company
              logo, colours, title-block layout and revision block are
              auto-filled from project data.
            </p>
            <div className="text-[10px] text-slate-500 mt-2 inline-flex items-center gap-1">
              <Info className="h-3 w-3" /> Supported: PDF · SVG · DXF · DWG · PNG · JPG
            </div>
          </div>
        </div>
      </div>

      {/* Upload */}
      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
            1 · Upload template file
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Apply to:</span>
            {APPLY_KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                onClick={() => setApplyTo(k.key)}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${
                  applyTo === k.key
                    ? "bg-fuchsia-500 text-slate-950"
                    : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                }`}
                data-testid={`apply-to-${k.key}`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>
        <label
          htmlFor="drawing-tpl-file"
          className={`flex items-center justify-center gap-3 rounded-lg border-2 border-dashed py-8 cursor-pointer transition ${
            uploading
              ? "border-fuchsia-800 bg-fuchsia-950/30"
              : "border-slate-700 hover:border-fuchsia-700 hover:bg-fuchsia-950/20"
          }`}
          data-testid="drawing-tpl-dropzone"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 text-fuchsia-400 animate-spin" />
              <span className="text-sm text-fuchsia-300">Analysing template…</span>
            </>
          ) : (
            <>
              <Upload className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-300">
                Drop or click to select .pdf / .svg / .dxf / .dwg / .png / .jpg
              </span>
            </>
          )}
          <input
            id="drawing-tpl-file"
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.svg,.dxf,.dwg,.png,.jpg,.jpeg,.webp"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid="drawing-tpl-file-input"
          />
        </label>
      </div>

      {/* List */}
      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
            2 · Available templates ({templates.length})
          </div>
          {loading && <Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" />}
        </div>
        {templates.length === 0 && !loading ? (
          <div className="text-xs text-slate-500 text-center py-6">
            No templates uploaded yet. Upload one above to get started.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="drawing-tpl-list">
            {templates.map((tpl) => {
              const isActive = !!tpl.active;
              const isPreview = previewingId === tpl.template_id;
              return (
                <li
                  key={tpl.template_id}
                  className={`rounded border p-3 transition ${
                    isActive
                      ? "border-emerald-800 bg-emerald-950/20"
                      : "border-slate-800 bg-slate-900/40 hover:bg-slate-900/70"
                  }`}
                  data-testid={`drawing-tpl-row-${tpl.template_id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {isActive && (
                        <Star className="h-3.5 w-3.5 text-emerald-400 fill-emerald-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-100 truncate">
                          {tpl.template_name}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {tpl.canvas_width}×{tpl.canvas_height} · {tpl.extracted_from} ·
                          {" "}Applies to <span className="text-slate-300">{tpl.apply_to}</span> ·
                          {" "}{Math.round((tpl.confidence || 0) * 100)}% conf
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handlePreview(tpl)}
                        className="text-xs inline-flex items-center gap-1 rounded px-2 py-1 bg-slate-800 text-slate-300 hover:bg-slate-700 transition"
                        data-testid={`preview-btn-${tpl.template_id}`}
                      >
                        <Eye className="h-3 w-3" />
                        {isPreview ? "Hide" : "Preview"}
                      </button>
                      {!isActive && (
                        <button
                          type="button"
                          onClick={() => handleActivate(tpl)}
                          className="text-xs inline-flex items-center gap-1 rounded px-2 py-1 bg-fuchsia-500 text-slate-950 hover:bg-fuchsia-400 transition font-semibold"
                          data-testid={`activate-btn-${tpl.template_id}`}
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Activate
                        </button>
                      )}
                      {isActive && (
                        <span className="text-[10px] font-semibold text-emerald-300 uppercase tracking-wider">
                          Active
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(tpl)}
                        className="text-slate-500 hover:text-rose-400 transition p-1"
                        data-testid={`delete-btn-${tpl.template_id}`}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  {isPreview && previewSvg && (
                    <div className="mt-3 rounded border border-slate-800 bg-slate-950 p-2 overflow-auto"
                         data-testid={`preview-svg-${tpl.template_id}`}>
                      <div
                        className="w-full [&>svg]:max-h-[400px] [&>svg]:max-w-full [&>svg]:h-auto"
                        dangerouslySetInnerHTML={{ __html: previewSvg }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {err && (
        <div className="text-xs text-rose-300 inline-flex items-start gap-2 rounded border border-rose-900/40 bg-rose-950/30 px-3 py-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" /> {err}
        </div>
      )}
      {ok && (
        <div className="text-xs text-emerald-200 inline-flex items-start gap-2 rounded border border-emerald-900/40 bg-emerald-950/30 px-3 py-2"
             data-testid="drawing-tpl-ok-banner">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" /> {ok}
        </div>
      )}
    </div>
  );
}
